import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {SchedulePolicy, useTimeout} from "@khanacademy/wonder-blocks-timing";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {LoadingDots} from "@khan/guide-ui";

import Shimmer from "~/components/shimmer-package/shimmer.tsx";

import {useCoeditingState} from "../use-coediting-state.ts";

import lightbulbIcon from "@phosphor-icons/core/regular/lightbulb.svg";

interface Props {
    placeholderKey: string;
    shape?: "heading" | "paragraph";
}

export default function PlaceholderBlock({
    placeholderKey,
    shape = "paragraph",
}: Props) {
    const {isLoading} = useCoeditingState();

    const [showTip, setShowTip] = React.useState(false);
    const [almostThere, setAlmostThere] = React.useState(false);
    const tipIndexRef = React.useRef<number>(0);

    const isCurrent = isLoading({placeholderKey});

    const {set: scheduleShowTip} = useTimeout(
        () => {
            setShowTip(true);
        },
        5000,
        {schedulePolicy: SchedulePolicy.OnDemand},
    );

    const {set: scheduleAlmostThere} = useTimeout(
        () => {
            setAlmostThere(true);
        },
        5000,
        {schedulePolicy: SchedulePolicy.OnDemand},
    );

    React.useEffect(() => {
        if (isCurrent) {
            tipIndexRef.current = Math.floor(Math.random() * tips().length);
            scheduleShowTip();
        }
    }, [isCurrent, scheduleShowTip]);

    React.useEffect(() => {
        if (isCurrent && showTip) {
            scheduleAlmostThere();
        }
    }, [isCurrent, showTip, scheduleAlmostThere]);

    if (shape === "heading") {
        return (
            <View style={styles.wrapper}>
                <Shimmer>
                    <View
                        style={[
                            styles.skeleton,
                            {
                                width: "60%",
                                height: sizing.size_400,
                            },
                        ]}
                    />
                </Shimmer>
            </View>
        );
    }

    return (
        <View style={styles.wrapper}>
            <Shimmer>
                <Skeleton />
            </Shimmer>
            <Shimmer>
                <Skeleton />
            </Shimmer>
            <Shimmer>
                <Skeleton />
            </Shimmer>
            {isCurrent && showTip && (
                <View style={styles.loading}>
                    <PhosphorIcon size="medium" icon={lightbulbIcon} />
                    <Body>{tips()[tipIndexRef.current]}</Body>
                </View>
            )}
            {isCurrent && showTip && almostThere && (
                <View style={styles.loading}>
                    <Body style={styles.loadingDots}>
                        <span>{t`Almost there`}</span>
                        <LoadingDots
                            color={semanticColor.status.neutral.foreground}
                            size="small"
                        />
                    </Body>
                </View>
            )}
        </View>
    );
}

const Skeleton = () => {
    return <View style={styles.skeleton} />;
};

function tips(): string[] {
    return [
        t`Try asking Khanmigo to break down a complex concept into smaller parts`,
        t`Ask Khanmigo to provide a simpler example to help students understand the concept`,
        t`Try asking Khanmigo to provide a more complex example to challenge students`,
        t`Ask Khanmigo to provide a real-world example to help students understand the concept`,
        t`Try asking Khanmigo to relate the topic to something students are familiar with`,
        t`Ask Khanmigo to include a reflection question`,
        t`Try asking Khanmigo to generate discussion questions for the class`,
        t`Ask Khanmigo to summarize the key points of the lesson`,
        t`Try asking Khanmigo to provide historical context for the lesson`,
    ];
}

const styles = StyleSheet.create({
    wrapper: {
        position: "relative",
        gap: sizing.size_080,
    },
    skeleton: {
        width: "100%",
        height: sizing.size_240,
        background: semanticColor.status.neutral.background,
        borderRadius: sizing.size_080,
    },
    loading: {
        flexDirection: "row",
        alignItems: "center",
        color: semanticColor.status.neutral.foreground,
        gap: sizing.size_080,
    },
    loadingDots: {
        display: "flex",
        flexDirection: "row",
        gap: sizing.size_040,
    },
});
